import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileCheck, Upload, Loader2, ExternalLink, Ban } from 'lucide-react';
import { toast } from 'sonner';

export default function OfflineCertsPage() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ certificate_code: '', item_title: '', brand_name: '', category_name: '' });
  const [file, setFile] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await api.get('/admin/certificates/offline'); setCerts(r.data); } catch {} finally { setLoading(false); }
  };

  const handleUpload = async () => {
    if (!form.certificate_code.trim()) return toast.error('Certificate code is required');
    if (!file) return toast.error('Choose a PDF file');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post('/admin/certificates/offline', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Certificate ${form.certificate_code.trim()} uploaded — verifiable at /verify`);
      setForm({ certificate_code: '', item_title: '', brand_name: '', category_name: '' });
      setFile(null);
      document.getElementById('offline-cert-file').value = '';
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const revoke = async (c) => {
    if (!window.confirm(`Revoke certificate ${c.certificate_code}? The verify page will show it as revoked.`)) return;
    try { await api.post(`/admin/certificates/offline/${c.id}/revoke`); toast.success('Certificate revoked'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="offline-certs-page">
      <div className="flex items-center gap-2 mb-4">
        <FileCheck className="h-4 w-4 text-ink" />
        <h1 className="text-base font-semibold text-ink">Offline Certificates</h1>
        <Badge variant="outline" className="text-[10px]">{certs.length}</Badge>
      </div>

      <Card className="bg-white border-stone/50 mb-4"><CardContent className="p-4">
        <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Upload a certificate created offline (PDF)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Certificate / Serial Number *</Label><Input value={form.certificate_code} onChange={e => setForm({...form, certificate_code: e.target.value})} placeholder="e.g. TFD-OFF-00123" className="mt-1 h-8 text-xs font-mono" data-testid="offline-cert-code-input" /></div>
          <div><Label className="text-xs">Item Title (shown on verify page)</Label><Input value={form.item_title} onChange={e => setForm({...form, item_title: e.target.value})} placeholder="e.g. Chanel Classic Flap Medium" className="mt-1 h-8 text-xs" data-testid="offline-cert-title-input" /></div>
          <div><Label className="text-xs">Brand</Label><Input value={form.brand_name} onChange={e => setForm({...form, brand_name: e.target.value})} className="mt-1 h-8 text-xs" data-testid="offline-cert-brand-input" /></div>
          <div><Label className="text-xs">Category</Label><Input value={form.category_name} onChange={e => setForm({...form, category_name: e.target.value})} className="mt-1 h-8 text-xs" data-testid="offline-cert-category-input" /></div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <input id="offline-cert-file" type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="offline-cert-file-input" />
          <Button size="sm" className="h-8 text-xs gap-1.5" onClick={handleUpload} disabled={uploading} data-testid="offline-cert-upload-btn">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload Certificate
          </Button>
        </div>
      </CardContent></Card>

      <Card className="bg-white border-stone/50"><CardContent className="p-0">
        {loading ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
        : certs.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No offline certificates uploaded yet.</p>
        : (
          <table className="w-full text-xs">
            <thead><tr className="border-b border-stone/30 text-left text-muted-foreground">
              <th className="p-2.5 font-medium">Code</th><th className="p-2.5 font-medium">Item</th><th className="p-2.5 font-medium">Brand</th><th className="p-2.5 font-medium">Uploaded</th><th className="p-2.5 font-medium">Status</th><th className="p-2.5 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {certs.map(c => (
                <tr key={c.id} className="border-b border-stone/20 hover:bg-stone/5" data-testid={`offline-cert-row-${c.certificate_code}`}>
                  <td className="p-2.5 font-mono">{c.certificate_code}</td>
                  <td className="p-2.5">{c.item_title || '—'}</td>
                  <td className="p-2.5">{c.brand_name || '—'}</td>
                  <td className="p-2.5 text-muted-foreground">{(c.issued_at || '').slice(0, 10)}</td>
                  <td className="p-2.5">{c.is_revoked ? <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700">Revoked</Badge> : <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700">Active</Badge>}</td>
                  <td className="p-2.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <a href={`/verify?code=${encodeURIComponent(c.certificate_code)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[11px]" style={{ color: '#1F5E7A' }} data-testid={`offline-cert-verify-link-${c.certificate_code}`}>Verify <ExternalLink className="h-3 w-3" /></a>
                      {c.pdf_url && <a href={c.pdf_url} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground underline" data-testid={`offline-cert-pdf-link-${c.certificate_code}`}>PDF</a>}
                      {!c.is_revoked && <button onClick={() => revoke(c)} className="inline-flex items-center gap-0.5 text-[11px] text-red-600 hover:underline" data-testid={`offline-cert-revoke-btn-${c.certificate_code}`}><Ban className="h-3 w-3" /> Revoke</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>
    </div>
  );
}
